import { useState, useRef, useCallback } from "react";
import "./DiagnosisView.css";
import MobileFrame from "../MobileFrame";
import { MOCK_FAULT_CODES, FaultCode } from "../repairData";

interface DiagnosisResult {
    fault: FaultCode;
    vehicleNumber: string;
}

interface DiagnosisViewProps {
    diagnosisResult: DiagnosisResult | null;
    setDiagnosisResult: (r: DiagnosisResult | null) => void;
    onNext: () => void;
}

const VEHICLE_OPTIONS = [
    { label: "12가3456", sub: "현대 포터II" },
    { label: "34나7890", sub: "기아 봉고III EV" },
    { label: "56다1234", sub: "현대 포터II" },
    { label: "78라5678", sub: "기아 봉고III EV" },
];

const SCENARIO_STEPS = ["경고등\n점등", "사진\n촬영", "앱\n업로드", "고장\n분류"];

type AppStep = 1 | 2 | 3 | 4;

// Fault light icons
function FaultIcon({ type, size = 28 }: { type: string; size?: number }) {
    const s = { width: size, height: size };
    switch (type) {
        case "engine": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="10" rx="2" /><path d="M7 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" /><path d="M13 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" /><path d="M22 10h1a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1" /><path d="M2 11H1a1 1 0 0 0-1 1v0a1 1 0 0 0 1 1h1" />
            </svg>
        );
        case "battery": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="6" width="18" height="12" rx="2" /><line x1="23" y1="13" x2="23" y2="11" /><line x1="7" y1="12" x2="11" y2="12" /><line x1="9" y1="10" x2="9" y2="14" />
            </svg>
        );
        case "coolant": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /><path d="M12 6v6l4 2" />
            </svg>
        );
        case "tire": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" />
            </svg>
        );
        case "oil": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M9 12h6" /><path d="M9 16h6" /><path d="M9 8h1" />
            </svg>
        );
        case "brake": return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="8" /><line x1="12" y1="16" x2="12" y2="22" /><line x1="2" y1="12" x2="8" y2="12" /><line x1="16" y1="12" x2="22" y2="12" />
            </svg>
        );
        default: return (
            <svg style={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
        );
    }
}

// ── Mobile App Content ────────────────────────────────────────────────────
function MobileAppContent({
    appStep, imageUrl, selectedVehicle,
    onSetVehicle, onFileChange, onStartAnalyze, analyzeStep, diagnosisResult, onNext
}: {
    appStep: AppStep;
    imageUrl: string | null;
    selectedVehicle: typeof VEHICLE_OPTIONS[0];
    onSetVehicle: (v: typeof VEHICLE_OPTIONS[0]) => void;
    onFileChange: (f: File) => void;
    onStartAnalyze: () => void;
    analyzeStep: number;
    diagnosisResult: DiagnosisResult | null;
    onNext: () => void;
}) {
    const fileRef = useRef<HTMLInputElement>(null);

    const stepStatus = (n: number): "done" | "active" | "pending" =>
        n < appStep ? "done" : n === appStep ? "active" : "pending";

    return (
        <>
            {/* App Header */}
            <div className="mapp-header gradient">
                <div className="mapp-header-text">
                    <div className="mapp-header-title">EView 정비</div>
                    <div className="mapp-header-sub">고장 진단 서비스</div>
                </div>
                <div className="mapp-logo-badge">EVIEW</div>
            </div>

            {/* Step Indicator */}
            <div className="mapp-steps">
                {SCENARIO_STEPS.map((label, i) => {
                    const n = i + 1;
                    const st = stepStatus(n);
                    return (
                        <div key={n} className={`mapp-step ${st}`}>
                            <div className={`mapp-step-dot ${st}`}>
                                {st === "done"
                                    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    : n}
                            </div>
                            <div className={`mapp-step-lbl ${st}`}>{label.split("\n")[0]}<br />{label.split("\n")[1]}</div>
                        </div>
                    );
                })}
            </div>

            {/* Step 1: Intro */}
            {appStep === 1 && (
                <>
                    <div className="mapp-intro">
                        <div className="mapp-intro-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                        </div>
                        <div className="mapp-intro-title">경고등이 켜졌나요?</div>
                        <div className="mapp-intro-sub">사진을 찍어 올리면<br />고장 유형을 자동으로 파악합니다</div>
                        <div className="mapp-intro-steps">
                            {["계기판 경고등 사진 촬영", "앱에 사진 업로드", "제조사 카탈로그 기반 자동 분류", "가까운 정비소 연결"].map((t, i) => (
                                <div key={i} className="mapp-intro-step">
                                    <div className="mapp-intro-step-num">{i + 1}</div>
                                    <div className="mapp-intro-step-text">{t}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mapp-cta">
                        <button className="mapp-cta-btn" onClick={() => onStartAnalyze()}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                            사진 촬영 시작
                        </button>
                    </div>
                </>
            )}

            {/* Step 2: Camera / Upload */}
            {appStep === 2 && (
                <>
                    <div style={{ flex: 1, overflow: "auto" }}>
                        {/* Vehicle selection */}
                        <div style={{ margin: "10px 10px 0", display: "flex", flexDirection: "column", gap: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", padding: "0 2px" }}>차량 선택</div>
                            <div style={{ background: "white", borderRadius: 10, overflow: "hidden" }}>
                                {VEHICLE_OPTIONS.map(v => (
                                    <div
                                        key={v.label}
                                        className="mapp-row"
                                        style={{ cursor: "pointer", background: selectedVehicle.label === v.label ? "#f0fdfa" : "white" }}
                                        onClick={() => onSetVehicle(v)}
                                    >
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{v.label}</div>
                                            <div style={{ fontSize: 11, color: "#6b7280" }}>{v.sub}</div>
                                        </div>
                                        {selectedVehicle.label === v.label && (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Upload zone */}
                        <div style={{ margin: "10px 10px 0", fontSize: 11, fontWeight: 600, color: "#6b7280", padding: "0 2px" }}>계기판 사진 업로드</div>
                        {!imageUrl ? (
                            <div className="mapp-upload-zone" onClick={() => fileRef.current?.click()}>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) onFileChange(f); }}
                                />
                                <svg className="mapp-upload-zone-icon" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                    <circle cx="12" cy="13" r="4" />
                                </svg>
                                <div className="mapp-upload-zone-text">
                                    <strong>탭하여 사진 선택</strong><br />
                                    계기판의 경고등이 보이도록 촬영
                                </div>
                            </div>
                        ) : (
                            <div className="mapp-preview-wrap">
                                <img src={imageUrl} alt="계기판" className="mapp-preview-img" />
                                <button className="mapp-preview-remove" onClick={() => onFileChange(null as any)}>✕</button>
                            </div>
                        )}
                    </div>
                    <div className="mapp-cta">
                        <button
                            className="mapp-cta-btn"
                            disabled={!imageUrl}
                            onClick={onStartAnalyze}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            업로드하기
                        </button>
                    </div>
                </>
            )}

            {/* Step 3: Analyzing */}
            {appStep === 3 && (
                <div className="mapp-loading">
                    <div className="mapp-spinner" />
                    <div className="mapp-loading-title">고장 유형 분석 중</div>
                    <div className="mapp-loading-sub">제조사 카탈로그 기반으로<br />경고등 유형을 분류하고 있습니다</div>
                    <div className="mapp-loading-steps">
                        {[
                            { label: "이미지 업로드 완료", done: analyzeStep >= 1 },
                            { label: "경고등 패턴 인식 중...", done: analyzeStep >= 2, active: analyzeStep === 1 },
                            { label: "제조사 카탈로그 조회 중...", done: analyzeStep >= 3, active: analyzeStep === 2 },
                        ].map((s, i) => (
                            <div key={i} className={`mapp-loading-step ${s.done ? "done" : s.active ? "active" : ""}`}>
                                {s.done ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                ) : s.active ? (
                                    <div style={{ width: 14, height: 14, border: "2px solid #0891b2", borderTopColor: "transparent", borderRadius: "50%", animation: "mapp-spin 0.8s linear infinite" }} />
                                ) : (
                                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#e5e5ea" }} />
                                )}
                                {s.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Step 4: Result */}
            {appStep === 4 && diagnosisResult && (
                <>
                    <div style={{ flex: 1, overflow: "auto" }}>
                        <div className="mapp-result-header">
                            <div className={`mapp-result-icon ${diagnosisResult.fault.severity}`}>
                                <FaultIcon type={diagnosisResult.fault.iconType} size={32} />
                            </div>
                            <div className="mapp-result-name">{diagnosisResult.fault.lightName}</div>
                            <div className="mapp-badge-row">
                                <span className="mapp-code-badge">{diagnosisResult.fault.code}</span>
                                <span className={`mapp-severity-badge ${diagnosisResult.fault.severity}`}>{diagnosisResult.fault.severity}</span>
                            </div>
                        </div>

                        {diagnosisResult.fault.severity === "위험" && (
                            <div className="mapp-alert">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                즉각적인 정비가 필요합니다
                            </div>
                        )}

                        <div className="mapp-card" style={{ margin: "8px 10px 10px" }}>
                            <div className="mapp-card-header">권장 조치</div>
                            <div style={{ padding: "10px 12px", fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
                                {diagnosisResult.fault.action}
                            </div>
                        </div>
                    </div>
                    <div className="mapp-cta">
                        <button className="mapp-cta-btn" onClick={onNext}>
                            가까운 정비소 찾기
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                            </svg>
                        </button>
                    </div>
                </>
            )}
        </>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function DiagnosisView({ diagnosisResult, setDiagnosisResult, onNext }: DiagnosisViewProps) {
    const [appStep, setAppStep] = useState<AppStep>(1);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [selectedVehicle, setSelectedVehicle] = useState(VEHICLE_OPTIONS[0]);
    const [analyzeStep, setAnalyzeStep] = useState(0);

    const handleFileChange = useCallback((file: File | null) => {
        if (!file) { setImageUrl(null); return; }
        if (!file.type.startsWith("image/")) return;
        const url = URL.createObjectURL(file);
        setImageUrl(url);
    }, []);

    const handleStartAnalyze = () => {
        if (appStep === 1) {
            setAppStep(2);
            return;
        }
        if (appStep === 2 && imageUrl) {
            setAppStep(3);
            setAnalyzeStep(0);
            setTimeout(() => setAnalyzeStep(1), 700);
            setTimeout(() => setAnalyzeStep(2), 1500);
            setTimeout(() => {
                const fault = MOCK_FAULT_CODES[Math.floor(Math.random() * MOCK_FAULT_CODES.length)];
                setDiagnosisResult({ fault, vehicleNumber: selectedVehicle.label });
                setAppStep(4);
            }, 2400);
        }
    };

    const scenarioStep = appStep === 1 ? 1 : appStep === 2 ? 2 : appStep === 3 ? 3 : 4;

    return (
        <div className="diagnosis-page">
            {/* ── Mobile Side ── */}
            <div className="diagnosis-mobile-side">
                <MobileFrame label="운전자 앱 화면">
                    <MobileAppContent
                        appStep={appStep}
                        imageUrl={imageUrl}
                        selectedVehicle={selectedVehicle}
                        onSetVehicle={setSelectedVehicle}
                        onFileChange={handleFileChange}
                        onStartAnalyze={handleStartAnalyze}
                        analyzeStep={analyzeStep}
                        diagnosisResult={diagnosisResult}
                        onNext={onNext}
                    />
                </MobileFrame>
            </div>

            {/* ── Web Side: System Result Panel ── */}
            <div className="diagnosis-web-side">
                {/* Scenario flow banner */}
                <div className="diag-scenario-banner">
                    <div className="diag-scenario-title">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        시나리오: 비실시간 정적 대응형 — 고장 분류 흐름
                    </div>
                    <div className="diag-scenario-steps">
                        {["경고등\n점등", "사진\n촬영", "앱\n업로드", "고장\n분류", "정비소\n추천", "정비소\n전송", "정비소\n이동"].map((label, i) => {
                            const n = i + 1;
                            const st = n < scenarioStep ? "done" : n === scenarioStep ? "active" : "pending";
                            return (
                                <div key={n} className={`diag-scenario-step ${st}`}>
                                    <div className={`diag-step-num ${st}`}>
                                        {st === "done"
                                            ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                            : n}
                                    </div>
                                    <div className={`diag-step-name ${st}`}>{label.split("\n")[0]}<br />{label.split("\n")[1]}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* System classification result */}
                <div className="diag-web-card" style={{ flex: 1 }}>
                    <div className="diag-web-header">
                        <span className="diag-web-header-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                            </svg>
                            고장 분류 시스템 결과
                        </span>
                        {diagnosisResult && (
                            <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
                                차량: {diagnosisResult.vehicleNumber}
                            </span>
                        )}
                    </div>

                    {!diagnosisResult ? (
                        <div className="diag-result-placeholder">
                            <svg className="diag-result-placeholder-icon" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="7" width="20" height="10" rx="2" /><path d="M7 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" /><path d="M13 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
                            </svg>
                            <div>
                                <strong style={{ color: "#6b7280" }}>앱에서 사진을 업로드하면 자동 분류됩니다</strong><br />
                                제조사 카탈로그 기반 고장 코드 데이터베이스를 조회하여<br />경고등 유형과 심각도를 자동으로 판독합니다
                            </div>
                            <div style={{ fontSize: 12, color: "#d1d5db" }}>현대·기아·타타대우·볼보 등 주요 상용차 지원</div>
                        </div>
                    ) : (
                        <div className="diag-result-content">
                            <div className="diag-fault-identity">
                                <div className={`diag-fault-icon ${diagnosisResult.fault.severity}`}>
                                    <FaultIcon type={diagnosisResult.fault.iconType} size={28} />
                                </div>
                                <div className="diag-fault-info">
                                    <div className="diag-fault-name">{diagnosisResult.fault.lightName}</div>
                                    <div className="diag-badge-row">
                                        <span className="diag-code-badge">{diagnosisResult.fault.code}</span>
                                        <span className={`diag-sev-badge ${diagnosisResult.fault.severity}`}>{diagnosisResult.fault.severity}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="diag-section">
                                <div className="diag-section-label">고장 설명</div>
                                <div className="diag-section-text">{diagnosisResult.fault.description}</div>
                            </div>

                            <div className="diag-section">
                                <div className="diag-section-label">권장 조치</div>
                                <div className="diag-section-text action">{diagnosisResult.fault.action}</div>
                            </div>

                            {diagnosisResult.fault.severity === "위험" && (
                                <div className="diag-danger-notice">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    즉각적인 정비가 필요한 상태입니다. 정비소 추천 화면으로 이동하세요.
                                </div>
                            )}

                            <button className="diag-next-btn" onClick={onNext}>
                                정비소 추천 받기
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
